/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowRight, Loader2, CheckCircle2, LogOut } from 'lucide-react';
import { sellerApi, getToken, clearSession, isReady } from '../sellerAuth';

const Activate = () => {
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadMe = async () => {
    try {
      const res = await sellerApi.get('/seller/me');
      setSeller(res.data);
      return res.data;
    } catch (e) {
      clearSession();
      navigate('/seller');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const poll = async (tries = 6) => {
    for (let i = 0; i < tries; i++) {
      const s = await loadMe();
      if (isReady(s)) { navigate('/app'); return; }
      await new Promise((r) => setTimeout(r, 2500));
    }
  };

  useEffect(() => {
    if (!getToken()) { navigate('/seller'); return; }
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected')) poll(); else loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startOnboarding = async () => {
    setBusy(true); setError('');
    try {
      const res = await sellerApi.post('/seller/onboarding-link', { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      setError('Could not open Stripe onboarding. Try again.');
      setBusy(false);
    }
  };

  const signOut = () => { clearSession(); navigate('/seller'); };

  useEffect(() => { if (isReady(seller)) navigate('/app'); /* eslint-disable-next-line */ }, [seller]);

  if (loading) return <div className="min-h-screen bg-[#0b1510] flex items-center justify-center"><Loader2 size={40} className="text-[#3ddc9a] animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0b1510] text-white flex flex-col px-7 py-10 max-w-md mx-auto">
      <div className="flex justify-between items-center mb-10">
        <span className="brand text-2xl">flix<span className="text-[#3ddc9a]">.</span></span>
        <button onClick={signOut} className="text-white/50 hover:text-[#ff6b5e] transition-colors"><LogOut size={18} /></button>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <div className="w-20 h-20 rounded-3xl bg-[#0e9f6e]/15 flex items-center justify-center mb-7"><ShieldCheck size={40} className="text-[#3ddc9a]" /></div>
        <h1 className="font-display text-3xl font-700">Complete your account</h1>
        <p className="text-white/55 mt-3 max-w-xs">Complete your account to start accepting payments. It only takes about 2 minutes with Stripe.</p>
        <div className="mt-6 space-y-2 w-full text-left">
          {[['Verify your identity', seller?.details_submitted], ['Enable card payments', seller?.card_payments_active], ['Enable payouts', seller?.transfers_active]].map(([label, done]) => (
            <div key={label} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3">
              <CheckCircle2 size={18} className={done ? 'text-[#3ddc9a]' : 'text-white/25'} />
              <span className={`text-sm ${done ? 'text-white' : 'text-white/55'}`}>{label}</span>
            </div>
          ))}
        </div>
        {error && <p className="text-sm text-[#ff6b5e] mt-4">{error}</p>}
      </div>
      <button data-testid="activate-stripe-btn" onClick={startOnboarding} disabled={busy} className="w-full bg-[#0e9f6e] text-white rounded-2xl py-4 text-base font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <>Continue with Stripe <ArrowRight size={18} /></>}
      </button>
      <button onClick={() => loadMe()} className="mt-4 text-sm text-white/55 text-center">I've finished — check again</button>
    </div>
  );
};

export default Activate;
